import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { AppSettings, AppSettingsService, FeatureService } from '@woolworthsnz/styleguide';
import { ExpressFulfilmentSettingsResponse, OrderViewModel, ContextResponse } from '@woolworthsnz/trader-api';
import { distinctUntilChanged } from 'rxjs/operators';
import { getExpressFee, getExpressFulfilmentMethodLabel, getMinimumTimeForExpressOrdersToBeReady } from '../../helpers';
import { FulfilmentStoreService } from '../../services';
import { NgIf, LowerCasePipe, CurrencyPipe } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
	selector: 'fulfilment-express-delivery',
	templateUrl: './express-fulfilment-delivery.component.html',
	styleUrls: ['./express-fulfilment-delivery.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: true,
	imports: [NgIf, LowerCasePipe, CurrencyPipe],
})
export class ExpressFulfilmentDeliveryComponent implements OnInit {
	expressFulfilmentSettings: ExpressFulfilmentSettingsResponse | undefined;
	fulfilmentMethod?: OrderViewModel.DeliveryMethodEnum;
	deliveryMethodEnum = OrderViewModel.DeliveryMethodEnum;
	isRebrandToDeliveryNowEnabled = false;
	appSettingServiceState$ = this.appSettingsService.state$.pipe(takeUntilDestroyed());
	featureServiceState$ = this.featureService.state$.pipe(takeUntilDestroyed());
	fulfilmentMethod$ = this.fulfilmentStoreService.select('method').pipe(takeUntilDestroyed(), distinctUntilChanged());
	allSlots$ = this.fulfilmentStoreService.select('slots').pipe(takeUntilDestroyed(), distinctUntilChanged());
	maxItemQuantityForExpressOrders: number | undefined;
	isUseItemsLimitFromFulfilmentSlotsEnabled = false;

	constructor(
		private cdr: ChangeDetectorRef,
		private appSettingsService: AppSettingsService,
		private fulfilmentStoreService: FulfilmentStoreService,
		private featureService: FeatureService
	) {}

	ngOnInit(): void {
		this.appSettingServiceState$.subscribe((appSettingsState: AppSettings) => {
			this.expressFulfilmentSettings = appSettingsState.expressFulfilmentSettings;
			this.cdr.markForCheck();
		});

		this.fulfilmentMethod$.subscribe((method) => {
			if (method) {
				this.fulfilmentMethod = method;
			}
			this.cdr.markForCheck();
		});

		this.featureServiceState$.subscribe(() => {
			this.isRebrandToDeliveryNowEnabled = this.featureService.isFeatureEnabled(
				ContextResponse.EnabledFeaturesEnum.EnableRebrandToDeliveryNow
			);

			this.isUseItemsLimitFromFulfilmentSlotsEnabled = this.featureService.isFeatureEnabled(
				ContextResponse.EnabledFeaturesEnum.UseItemsLimitFromFulfilmentSlots
			);

			this.cdr.markForCheck();
		});

		this.allSlots$.subscribe((slots) => {
			this.maxItemQuantityForExpressOrders = slots?.find((slot) => slot.isExpress)?.itemsLimit;
			this.cdr.markForCheck();
		});
	}

	getItemsLimit(): number | undefined {
		return this.isUseItemsLimitFromFulfilmentSlotsEnabled
			? this.maxItemQuantityForExpressOrders
			: this.expressFulfilmentSettings?.maxItemQuantityForExpressOrders;
	}

	get fulfilmentMethodLabel(): string {
		if (
			this.isRebrandToDeliveryNowEnabled &&
			getExpressFulfilmentMethodLabel(this.fulfilmentMethod) === 'Delivery'
		) {
			return 'Delivery Now';
		}

		return `Express ${getExpressFulfilmentMethodLabel(this.fulfilmentMethod)}`;
	}

	get deliveryMethodLabel(): string {
		return this.isRebrandToDeliveryNowEnabled ? 'Delivery Now orders' : 'Express deliveries';
	}

	get expressReadyTime(): number {
		return getMinimumTimeForExpressOrdersToBeReady(this.fulfilmentMethod, this.expressFulfilmentSettings);
	}

	get expressFee(): number | undefined {
		return getExpressFee(this.fulfilmentMethod, this.expressFulfilmentSettings);
	}
}
